// src/app/core/store/person/person.effects.ts

import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, finalize, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { PersonApiService } from '@core/services/person-api.service';
import { isCacheValid } from './person.state';
import { PersonByIdActions, PersonByIdNoActions, PersonSearchActions } from './person.actions';
import { selectPersonByIdMap, selectPersonByIdNoMap } from './person.selectors';

@Injectable()
export class PersonEffects {
  private readonly actions$   = inject(Actions);
  private readonly store      = inject(Store);
  private readonly api        = inject(PersonApiService);
  private readonly translate  = inject(TranslateService);

  // Keyed in-flight dedup sets
  private readonly _inFlightById    = new Set<number>();
  private readonly _inFlightByIdNo  = new Set<string>();

  private readError(err: unknown): string {
    const e = err as any;
    return e?.error?.message ?? e?.message ?? this.translate.instant('ERRORS.UNEXPECTED_DOT');
  }

  // ── Search (switchMap — auto-cancels previous) ──────────────────────────────
  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonSearchActions.search),
      switchMap(({ params }) =>
        this.api.search(params).pipe(
          map(data  => PersonSearchActions.searchSuccess({ data })),
          catchError(err => of(PersonSearchActions.searchFailure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Get by id ───────────────────────────────────────────────────────────────
  loadById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonByIdActions.load),
      withLatestFrom(this.store.select(selectPersonByIdMap)),
      switchMap(([{ id, forceReload }, byIdMap]) => {
        const slice = byIdMap[id];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        if (this._inFlightById.has(id)) return EMPTY;
        this._inFlightById.add(id);
        return this.api.getById(id).pipe(
          map(data  => PersonByIdActions.success({ id, data })),
          catchError(err => of(PersonByIdActions.failure({ id, error: this.readError(err) }))),
          finalize(() => this._inFlightById.delete(id)),
        );
      }),
    )
  );

  // ── Get by IDNo ─────────────────────────────────────────────────────────────
  loadByIdNo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PersonByIdNoActions.load),
      withLatestFrom(this.store.select(selectPersonByIdNoMap)),
      switchMap(([{ idno, forceReload }, byIdNoMap]) => {
        const slice = byIdNoMap[idno];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        if (this._inFlightByIdNo.has(idno)) return EMPTY;
        this._inFlightByIdNo.add(idno);
        return this.api.getByIdNo(idno).pipe(
          map(data  => PersonByIdNoActions.success({ idno, data })),
          catchError(err => of(PersonByIdNoActions.failure({ idno, error: this.readError(err) }))),
          finalize(() => this._inFlightByIdNo.delete(idno)),
        );
      }),
    )
  );
}
